import { fetchMarketdataCallsBatch } from "../api/marketdataCalls";
import { applyTickerEarnings } from "./callsRiskyStrike";
import {
  bindFocusRowMenuDocumentHandlers,
  renderFocusRecRowMenu,
  setFocusActionsRefresh,
  syncFocusRowMenuAfterDomUpdate,
} from "./focusActions";
import {
  ensureColumnPickerBesideReset,
  getTableColumnDefaults,
  loadTableColumns,
  renderTableColgroup,
  renderTableHeadRow,
  tableColumnTitle,
  visibleTableColumns,
  type ColumnState,
} from "./tableColumns";
import { showToast } from "./toast";
import {
  accountShortCellHtml,
  brokerMarkCellHtml,
  ensureLedgerMarkIndex,
  recommendationLedgerMark,
  subscribeLedgerMarks,
} from "./ledgerMarks";
import { parseRecommendationFromRow } from "./tableContextMenu";
import {
  callStrikeVsAvgCost,
  callStrikeVsAvgTip,
  callStrikeVsAvgValueClass,
  renderCallStrikeTipPopover,
  callContractsAvailable,
  esc,
  fmtNum,
  fmtPct,
  fmtUsd,
  fmtWheelRatio,
  loadPositionsSnapshot,
  loadRecommendationsSnapshot,
  normalizeTicker,
  roiClass,
  shareAvgFromStock,
  sharesAvailable,
  type OptionPosition,
  type StockPosition,
  type WheelRecommendation,
} from "./watchlistData";

const WHEEL_API_BASE = "/api/v1/wheel";
const TXN_PAGE_LIMIT = 500;
const TXN_MAX_PAGES = 40;
const DATA_CACHE_MS = 60_000;
const CALLS_PLUS_MIN_POP_KEY = "sfe-calls-plus-min-pop";
const CALLS_PLUS_MIN_POP_DEFAULT = 0;
const CALLS_PLUS_MAX_DTE_KEY = "sfe-calls-plus-max-dte";
/** Default shows primary (≤7) + secondary (≤15) short-dated focus. */
const CALLS_PLUS_MAX_DTE_DEFAULT = 15;
const CALLS_PLUS_COLLAPSED_KEY = "sfe-calls-plus-collapsed-tickers";

/** Preferred weekly window — ranked first. */
export const CALLS_PLUS_DTE_PRIMARY = 7;
/** Biweekly window — ranked after primary; default Max DTE. */
export const CALLS_PLUS_DTE_SECONDARY = 15;

interface WheelTransaction {
  id?: number;
  linked_transaction_id?: number;
  wheel_pair_linked_ids?: number[];
  inferred_linked_transaction_ids?: number[];
  instrument_type?: string;
  action?: string;
  side?: string;
  ticker?: string;
  trade_date?: string;
  trade_timestamp?: string;
  quantity?: number;
  avg_price?: number;
  strike?: number;
  exp_date?: string;
  close_reason?: string;
  option_trans_code?: string;
  option_symbol?: string;
  total_credit?: number;
  assignment_companion?: boolean;
  /** Underlying share price recorded on this row (own ledger). */
  underlying_price?: number;
  /** Provenance of underlying_price: md_fill_minute, md_day_close, md_day_close_estimated. */
  underlying_price_source?: string;
  /** Underlying price where the contract was opened, read through the open_close link. */
  underlying_open_price?: number;
  /** Underlying price where the contract closed, expired or was assigned. */
  underlying_close_price?: number;
  /** POP (1 − |delta|) when the contract was sold; closes read their opening's. */
  open_pop?: number;
  /** md_delta when captured, not_captured for contracts opened before capture existed. */
  open_pop_source?: string;
  /** Synthetic stock leg from expandImpliedStockLegs (not in API). */
  implied_stock_leg?: boolean;
  account_id?: string;
  account_label?: string;
  account_name?: string;
  account_short_name?: string;
  broker_id?: string;
  broker_name?: string;
  source?: string;
  extensions?: Record<string, string>;
  tags?: string[];
  notes_preview?: string;
  notes?: Array<{ body?: string | null; kind?: string | null }>;
  note?: string;
}

interface AvgPosition {
  qty: number;
  avg: number;
}

export interface LotLedgerEvent {
  date: string;
  kind: "buy" | "put_assignment" | "call_assignment" | "stock_sell";
  label: string;
  shares: number;
  price: number;
  netShares: number;
  avgPrice: number;
}

export interface UnassignedCallRecord {
  openDate: string;
  closeDate: string;
  closeLabel: string;
  strike: number;
  expiration: string;
  contracts: number;
  openCredit: number;
  closeCost: number;
  netPremium: number;
}

export interface TickerLotContext {
  lotStartDate: string;
  lotLedger: LotLedgerEvent[];
  callPremiumCollected: number;
  unassignedCalls: UnassignedCallRecord[];
}

export interface BuildTickerLotOptions {
  /** Keep all share adds/sells across zero-share resets (share history UI). */
  preserveShareHistory?: boolean;
}

export interface CallsPlusRow {
  rec: WheelRecommendation;
  ticker: string;
  shares: number;
  avgCost: number;
  currentPrice: number;
  atRiskAmt: number;
  evenPrice: number;
  cumulativeProfit: number;
  lot: TickerLotContext;
}

export interface CallsPlusSnapshot {
  rows: CallsPlusRow[];
  blockedTickers: number;
  totalUnderwater: number;
  totalPremium: number;
  diagnostics: CallsPlusDiagnostics;
}

export interface CallsPlusDiagnostics {
  stockPositions: number;
  underwaterTickers: number;
  blockedTickers: number;
  callRecommendations: number;
  riskyRecommendations: number;
  marketdata?: {
    requestedTickers: number;
    creditsUsed: number;
    dailyCreditsRemaining?: number;
    addedRecs: number;
    cachedTickers?: number;
  };
}

function recKey(rec: WheelRecommendation): string {
  return `${normalizeTicker(rec.underlying_ticker)}|${rec.strike}|${rec.expiration_date ?? ""}`;
}

export function mergeCallRecommendations(...sources: WheelRecommendation[][]): WheelRecommendation[] {
  const map = new Map<string, WheelRecommendation>();
  for (const rec of sources.flat()) {
    const key = recKey(rec);
    const prev = map.get(key);
    map.set(key, prev ? { ...prev, ...rec } : rec);
  }
  return [...map.values()];
}

function parseCallsTableDom(): WheelRecommendation[] {
  const table = document.getElementById("wheelCallsTable");
  if (!(table instanceof HTMLTableElement)) return [];

  const rows: WheelRecommendation[] = [];
  for (const tr of table.querySelectorAll("tbody tr")) {
    if (!(tr instanceof HTMLTableRowElement)) continue;
    const rec = parseRecommendationFromRow(tr, "call");
    if (rec) rows.push(rec);
  }
  return rows;
}

function callsForTicker(calls: WheelRecommendation[], ticker: string): WheelRecommendation[] {
  const key = normalizeTicker(ticker);
  return calls.filter((rec) => normalizeTicker(rec.underlying_ticker) === key);
}

function hasBelowAvgCallRec(
  tickerCalls: WheelRecommendation[],
  avgCost: number,
  maxDte = CALLS_PLUS_DTE_SECONDARY,
): boolean {
  return tickerCalls.some((rec) => {
    const strike = Number(rec.strike);
    return (
      Number.isFinite(strike) &&
      callStrikeVsAvgCost(strike, avgCost) === "below" &&
      meetsMaxDte(rec, maxDte)
    );
  });
}

export function blockedPositionsNeedingMarketdata(
  stock: StockPosition[],
  options: OptionPosition[],
  calls: WheelRecommendation[],
): Array<{ ticker: string; avgCost: number; currentPrice: number }> {
  const needs: Array<{ ticker: string; avgCost: number; currentPrice: number }> = [];

  for (const position of stock) {
    const ticker = normalizeTicker(position.ticker);
    if (!ticker) continue;

    const tickerCalls = callsForTicker(calls, ticker);
    const blocked = evaluateCallsPlusTicker(position, options, tickerCalls);
    if (!blocked) continue;
    // Still fetch MD when only longer-dated below-avg RH recs exist.
    if (hasBelowAvgCallRec(tickerCalls, blocked.avgCost, CALLS_PLUS_DTE_SECONDARY)) continue;

    needs.push({
      ticker,
      avgCost: blocked.avgCost,
      currentPrice: blocked.currentPrice,
    });
  }

  return needs;
}

function resolveAvgCost(
  stock: StockPosition,
  tickerCalls: WheelRecommendation[],
): number | undefined {
  const fromStock = shareAvgFromStock(stock);
  if (fromStock != null) return fromStock;
  return tickerCalls.find(
    (rec) => rec.holding_avg_cost != null && Number.isFinite(rec.holding_avg_cost),
  )?.holding_avg_cost;
}

function resolveCurrentPrice(
  ticker: string,
  stock: StockPosition,
  options: OptionPosition[],
  tickerCalls: WheelRecommendation[],
): number | undefined {
  const direct = Number(stock.current_price);
  if (Number.isFinite(direct) && direct > 0) return direct;

  for (const rec of tickerCalls) {
    const price = Number(rec.underlying_price);
    if (Number.isFinite(price) && price > 0) return price;
  }

  for (const opt of options) {
    if (normalizeTicker(opt.ticker) !== ticker) continue;
    const price = Number(opt.stock_price);
    if (Number.isFinite(price) && price > 0) return price;
  }

  return undefined;
}

function optionsForTicker(options: OptionPosition[], ticker: string): OptionPosition[] {
  const key = normalizeTicker(ticker);
  return options.filter((opt) => normalizeTicker(opt.ticker) === key);
}

function sharesNotUnderContract(
  stock: StockPosition,
  options: OptionPosition[],
  ticker: string,
): number | null {
  return sharesAvailable(stock, optionsForTicker(options, ticker));
}

function evaluateCallsPlusTicker(
  stock: StockPosition,
  options: OptionPosition[],
  tickerCalls: WheelRecommendation[],
): { avgCost: number; currentPrice: number; shares: number } | null {
  const ticker = normalizeTicker(stock.ticker);
  if (!ticker || (Number(stock.quantity) || 0) <= 0) return null;

  const available = sharesNotUnderContract(stock, options, ticker);
  if (available == null || available <= 0) return null;

  const avgCost = resolveAvgCost(stock, tickerCalls);
  const currentPrice = resolveCurrentPrice(ticker, stock, options, tickerCalls);
  if (avgCost == null || currentPrice == null || currentPrice >= avgCost) return null;

  return { avgCost, currentPrice, shares: available };
}

async function wheelFetch<T = unknown>(path: string): Promise<T> {
  const fullPath = `${WHEEL_API_BASE}${path}`;

  const pageApi = (window as unknown as { API?: { get: (p: string, o?: object) => Promise<T> } }).API;
  if (pageApi?.get) {
    return pageApi.get(fullPath, { silent: true });
  }

  const res = await fetch(fullPath, {
    credentials: "include",
    headers: { Accept: "application/json" },
  });
  if (res.ok) return res.json() as Promise<T>;

  throw new Error(`${path}: fetch failed`);
}

function wheelTxnFetchErrorStatus(err: unknown): number | undefined {
  if (err instanceof Error && "status" in err && typeof err.status === "number") {
    return err.status;
  }
  return undefined;
}

export async function fetchCallsPlusTransactions(
  get: <T>(path: string, force?: boolean) => Promise<T>,
  force = false,
): Promise<WheelTransaction[]> {
  try {
    const data = await get<{ transactions?: Record<string, unknown>[] }>(
      `/transactions-snapshot`,
      force,
    );
    return (data.transactions ?? [])
      .map(normalizeTransaction)
      .sort((a, b) => String(a.trade_date).localeCompare(String(b.trade_date)));
  } catch (err) {
    // Only fall back when the snapshot route is missing (e.g. native Stock Forge host).
    // Other failures should not fan out into many paginated upstream calls.
    if (wheelTxnFetchErrorStatus(err) !== 404) throw err;
  }

  const txns: WheelTransaction[] = [];
  let offset = 0;

  for (let page = 0; page < TXN_MAX_PAGES; page += 1) {
    const data = await get<{ transactions?: Record<string, unknown>[] }>(
      `/transactions?limit=${TXN_PAGE_LIMIT}&offset=${offset}`,
      force,
    );
    const batch = (data.transactions ?? []).map(normalizeTransaction);
    txns.push(...batch);
    if (batch.length < TXN_PAGE_LIMIT) break;
    offset += TXN_PAGE_LIMIT;
  }

  return txns.sort((a, b) => String(a.trade_date).localeCompare(String(b.trade_date)));
}

async function fetchTransactions(): Promise<WheelTransaction[]> {
  return fetchCallsPlusTransactions((path) => wheelFetch(path));
}

function normalizeTransaction(raw: Record<string, unknown>): WheelTransaction {
  const id = raw.id != null ? Number(raw.id) : undefined;
  const linkedId =
    raw.linked_transaction_id != null ? Number(raw.linked_transaction_id) : undefined;
  const wheelPairRaw = raw.wheel_pair_linked_ids;
  const wheelPairLinkedIds = Array.isArray(wheelPairRaw)
    ? wheelPairRaw.map((id) => Number(id)).filter((id) => Number.isFinite(id) && id > 0)
    : undefined;
  const qty = raw.quantity != null ? Number(raw.quantity) : undefined;
  const avg = raw.avg_price != null ? Number(raw.avg_price) : undefined;
  const strike = raw.strike != null ? Number(raw.strike) : undefined;
  const num = (value: unknown): number | undefined => {
    if (value == null) return undefined;
    const parsed = Number(value);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : undefined;
  };
  let credit: number | undefined;
  if (raw.total_credit != null) {
    const top = Number(raw.total_credit);
    if (Number.isFinite(top)) credit = top;
  }
  if (credit == null && raw.extensions && typeof raw.extensions === "object" && !Array.isArray(raw.extensions)) {
    const nested = Number((raw.extensions as Record<string, unknown>).total_credit);
    if (Number.isFinite(nested)) credit = nested;
  }

  return {
    id: Number.isFinite(id) ? id : undefined,
    linked_transaction_id: Number.isFinite(linkedId) ? linkedId : undefined,
    wheel_pair_linked_ids:
      wheelPairLinkedIds && wheelPairLinkedIds.length ? wheelPairLinkedIds : undefined,
    instrument_type:
      typeof raw.instrument_type === "string" ? raw.instrument_type : undefined,
    action: typeof raw.action === "string" ? raw.action : undefined,
    side: typeof raw.side === "string" ? raw.side : undefined,
    ticker: typeof raw.ticker === "string" ? raw.ticker : undefined,
    trade_date: typeof raw.trade_date === "string" ? raw.trade_date : undefined,
    trade_timestamp: typeof raw.trade_timestamp === "string" ? raw.trade_timestamp : undefined,
    quantity: Number.isFinite(qty) ? qty : undefined,
    avg_price: Number.isFinite(avg) ? avg : undefined,
    strike: Number.isFinite(strike) ? strike : undefined,
    exp_date: typeof raw.exp_date === "string" ? raw.exp_date : undefined,
    close_reason: typeof raw.close_reason === "string" ? raw.close_reason : undefined,
    option_trans_code:
      typeof raw.option_trans_code === "string" ? raw.option_trans_code : undefined,
    option_symbol: typeof raw.option_symbol === "string" ? raw.option_symbol : undefined,
    total_credit: Number.isFinite(credit) ? credit : undefined,
    underlying_price: num(raw.underlying_price),
    underlying_price_source:
      typeof raw.underlying_price_source === "string" ? raw.underlying_price_source : undefined,
    underlying_open_price: num(raw.underlying_open_price),
    underlying_close_price: num(raw.underlying_close_price),
    open_pop: num(raw.open_pop),
    open_pop_source: typeof raw.open_pop_source === "string" ? raw.open_pop_source : undefined,
    account_id: typeof raw.account_id === "string" ? raw.account_id : undefined,
    account_label: typeof raw.account_label === "string" ? raw.account_label : undefined,
    account_name: typeof raw.account_name === "string" ? raw.account_name : undefined,
    account_short_name:
      typeof raw.account_short_name === "string" ? raw.account_short_name : undefined,
    broker_id: typeof raw.broker_id === "string" ? raw.broker_id : undefined,
    broker_name: typeof raw.broker_name === "string" ? raw.broker_name : undefined,
    source: typeof raw.source === "string" ? raw.source : undefined,
    extensions:
      raw.extensions && typeof raw.extensions === "object" && !Array.isArray(raw.extensions)
        ? (raw.extensions as Record<string, string>)
        : undefined,
    tags: Array.isArray(raw.tags) ? raw.tags.map(String) : undefined,
    notes: Array.isArray(raw.notes)
      ? raw.notes.map((row) => {
          const rec = row && typeof row === "object" ? (row as Record<string, unknown>) : {};
          return {
            body: typeof rec.body === "string" ? rec.body : null,
            kind: typeof rec.kind === "string" ? rec.kind : null,
          };
        })
      : undefined,
    notes_preview: typeof raw.notes_preview === "string" ? raw.notes_preview : undefined,
  };
}

function txnCode(txn: WheelTransaction): string {
  return (txn.option_trans_code ?? "").toUpperCase();
}

function txnContracts(txn: WheelTransaction): number {
  return Number(txn.quantity) || 0;
}

function txnNotional(txn: WheelTransaction): number {
  const credit = Number(txn.total_credit);
  if (Number.isFinite(credit) && credit !== 0) return Math.abs(credit);
  const qty = txnContracts(txn);
  const price = Number(txn.avg_price) || 0;
  if (qty > 0 && price > 0) return qty * price * 100;
  return 0;
}

function contractKey(txn: WheelTransaction): string | null {
  if (txn.instrument_type !== "option" || !txn.ticker) return null;
  return `${txn.ticker}|${txn.side ?? ""}|${txn.strike ?? ""}|${txn.exp_date ?? ""}`;
}

function isCallAssignmentTxn(txn: WheelTransaction): boolean {
  return txn.instrument_type === "option" && txn.side === "call" && isAssignedOptionTxn(txn);
}

function isPutAssignmentTxn(txn: WheelTransaction): boolean {
  return txn.instrument_type === "option" && txn.side === "put" && isAssignedOptionTxn(txn);
}

function isAssignedOptionTxn(txn: WheelTransaction): boolean {
  return (
    txn.instrument_type === "option" &&
    (txn.close_reason === "assigned" ||
      txnCode(txn) === "OASGN" ||
      txnCode(txn) === "ASGN")
  );
}

function isExpiredOptionTxn(txn: WheelTransaction): boolean {
  return (
    txn.instrument_type === "option" &&
    (txn.close_reason === "expired" || txnCode(txn) === "OEXP")
  );
}

/** Infer assignment markers and companion stock legs before share-history expansion. */
export function inferAssignmentTransactions(txns: WheelTransaction[]): WheelTransaction[] {
  return inferAssignmentsFromStockLegs(txns);
}

function blendBuy(pos: AvgPosition, qty: number, price: number): void {
  if (qty <= 0) return;
  const totalCost = pos.qty * pos.avg + qty * price;
  pos.qty += qty;
  pos.avg = pos.qty > 0 ? totalCost / pos.qty : 0;
}

function reduceQty(pos: AvgPosition, qty: number): void {
  if (qty <= 0) return;
  pos.qty = Math.max(0, pos.qty - qty);
  if (pos.qty === 0) pos.avg = 0;
}

function shareAvgBookKey(txn: Pick<WheelTransaction, "account_id">): string {
  return String(txn.account_id ?? "").trim() || "_";
}

function shareAvgPositionKey(
  txn: Pick<WheelTransaction, "account_id" | "ticker">,
  ticker = txn.ticker ?? "",
): string {
  return `${shareAvgBookKey(txn)}|${normalizeTicker(ticker)}`;
}

function applyAvgCostEvent(txn: WheelTransaction, positions: Map<string, AvgPosition>): void {
  if (txn.instrument_type === "stock" && txn.ticker) {
    if (txn.assignment_companion) return;
    const key = shareAvgPositionKey(txn);
    const pos = positions.get(key) ?? { qty: 0, avg: 0 };
    const qty = Number(txn.quantity) || 0;
    const price = Number(txn.avg_price) || 0;
    if (txn.action === "buy") blendBuy(pos, qty, price);
    else if (txn.action === "sell") reduceQty(pos, qty);
    positions.set(key, pos);
    return;
  }

  if (isPutAssignmentTxn(txn) && txn.ticker) {
    const key = shareAvgPositionKey(txn);
    const pos = positions.get(key) ?? { qty: 0, avg: 0 };
    const contracts = Number(txn.quantity) || 0;
    const strike = Number(txn.strike) || 0;
    if (contracts > 0 && strike > 0) {
      blendBuy(pos, Math.round(contracts * 100), strike);
    }
    positions.set(key, pos);
    return;
  }

  if (!isCallAssignmentTxn(txn) || !txn.ticker) return;

  const key = shareAvgPositionKey(txn);
  const pos = positions.get(key) ?? { qty: 0, avg: 0 };
  const contracts = Number(txn.quantity) || 0;
  reduceQty(pos, Math.round(contracts * 100));
  positions.set(key, pos);
}

function inferAssignmentsFromStockLegs(txns: WheelTransaction[]): WheelTransaction[] {
  const adjusted = txns.map((txn) => ({ ...txn }));
  const byId = new Map(
    adjusted
      .filter((txn): txn is WheelTransaction & { id: number } => txn.id != null)
      .map((txn) => [txn.id, txn]),
  );
  const stockLegs = new Map<string, Array<{ txn: WheelTransaction; remaining: number }>>();

  for (const txn of adjusted) {
    if (txn.instrument_type !== "stock" || !txn.ticker || !txn.trade_date) continue;
    if (txn.action !== "buy" && txn.action !== "sell") continue;
    const quantity = Number(txn.quantity) || 0;
    if (quantity <= 0) continue;
    const key = `${txn.trade_date}|${txn.ticker}|${txn.action}`;
    const legs = stockLegs.get(key) ?? [];
    legs.push({ txn, remaining: quantity });
    stockLegs.set(key, legs);
  }

  for (const txn of adjusted) {
    if (txn.instrument_type !== "option" || !txn.ticker || !txn.trade_date) continue;
    if (txn.side !== "put" && txn.side !== "call") continue;
    if (isExpiredOptionTxn(txn)) continue;

    const explicitAssignment = isAssignedOptionTxn(txn);
    // Zero-cost settlement inference applies to the usual BTC/assignment buy leg only.
    if (!explicitAssignment && txn.action !== "buy") continue;

    const contracts = txnContracts(txn);
    const shares = Math.round(contracts * 100);
    if (shares <= 0) continue;

    const zeroCostSettlement =
      txnNotional(txn) === 0 &&
      txn.exp_date != null &&
      txn.trade_date >= txn.exp_date;

    const linked =
      (txn.linked_transaction_id != null
        ? byId.get(txn.linked_transaction_id)
        : undefined) ??
      adjusted.find(
        (candidate) =>
          txn.id != null &&
          candidate.linked_transaction_id === txn.id &&
          candidate.instrument_type === "stock",
      );
    const stockAction = txn.side === "put" ? "buy" : "sell";
    const linkedStock =
      linked?.instrument_type === "stock" &&
      linked.action === stockAction &&
      linked.ticker === txn.ticker &&
      (!linked.trade_date || linked.trade_date === txn.trade_date) &&
      (Number(linked.quantity) || 0) >= shares
        ? linked
        : undefined;

    const key = `${txn.trade_date}|${txn.ticker}|${stockAction}`;
    const quantityMatched = (stockLegs.get(key) ?? []).find((leg) => leg.remaining >= shares);
    const companion = linkedStock
      ? { txn: linkedStock, remaining: Number(linkedStock.quantity) || shares }
      : quantityMatched;

    // Explicit assignment rows stand alone even when no stock leg was recorded.
    if (
      !explicitAssignment &&
      !linkedStock &&
      !(zeroCostSettlement && quantityMatched)
    ) {
      continue;
    }

    txn.close_reason = "assigned";
    txn.option_trans_code = "OASGN";
    if (companion) {
      if (companion === quantityMatched) companion.remaining -= shares;
      companion.txn.assignment_companion = true;
      if (txn.linked_transaction_id == null && txn.id != null && companion.txn.id != null) {
        txn.inferred_linked_transaction_ids = [
          ...new Set([...(txn.inferred_linked_transaction_ids ?? []), companion.txn.id]),
        ];
        companion.txn.inferred_linked_transaction_ids = [
          ...new Set([...(companion.txn.inferred_linked_transaction_ids ?? []), txn.id]),
        ];
      }
    }
  }

  return adjusted;
}

function findLinkedAssignmentOption(
  stockTxn: WheelTransaction,
  all: WheelTransaction[],
  byId: Map<number, WheelTransaction>,
): WheelTransaction | undefined {
  const stockId = Number(stockTxn.id);
  const linkedId = Number(stockTxn.linked_transaction_id);
  if (Number.isFinite(linkedId) && linkedId > 0) {
    const linked = byId.get(linkedId);
    if (linked?.instrument_type === "option" && isAssignedOptionTxn(linked)) return linked;
  }

  if (Number.isFinite(stockId) && stockId > 0) {
    for (const other of all) {
      if (other.instrument_type !== "option") continue;
      if (Number(other.linked_transaction_id) !== stockId) continue;
      if (isAssignedOptionTxn(other)) return other;
    }
    for (const inferredId of stockTxn.inferred_linked_transaction_ids ?? []) {
      const linked = byId.get(inferredId);
      if (linked?.instrument_type === "option" && isAssignedOptionTxn(linked)) return linked;
    }
  }

  for (const other of all) {
    if (other.instrument_type !== "option" || !isAssignedOptionTxn(other)) continue;
    if (other.ticker !== stockTxn.ticker || other.trade_date !== stockTxn.trade_date) continue;
    const stockAction = other.side === "put" ? "buy" : "sell";
    if (stockTxn.action !== stockAction) continue;
    const shares = Math.round(txnContracts(other) * 100);
    if (shares > 0 && (Number(stockTxn.quantity) || 0) >= shares) return other;
  }

  return undefined;
}

function shareEventKey(date: string, shares: number, kind: string): string {
  return `${date}|${shares}|${kind}`;
}

function classifyOptionTxns(txns: WheelTransaction[]): WheelTransaction[] {
  const adjusted = inferAssignmentsFromStockLegs(txns);
  const open = new Map<string, { shortQty: number; longQty: number }>();

  for (const txn of adjusted) {
    if (txn.instrument_type !== "option") continue;
    const key = contractKey(txn);
    if (!key) continue;

    const qty = txnContracts(txn);
    if (qty <= 0) continue;

    const pos = open.get(key) ?? { shortQty: 0, longQty: 0 };
    const code = txnCode(txn);

    if (txn.close_reason === "assigned") {
      pos.shortQty = Math.max(0, pos.shortQty - qty);
      open.set(key, pos);
      continue;
    }

    if (txn.close_reason === "expired" || code === "OEXP") {
      txn.option_trans_code = "OEXP";
      pos.shortQty = Math.max(0, pos.shortQty - qty);
      open.set(key, pos);
      continue;
    }

    if (txn.action === "sell") {
      if (code === "STC" || (!code && pos.longQty >= qty)) {
        txn.option_trans_code = "STC";
        pos.longQty = Math.max(0, pos.longQty - qty);
      } else {
        txn.option_trans_code = "STO";
        pos.shortQty += qty;
      }
    } else if (txn.action === "buy") {
      if (code === "BTO" || (!code && pos.shortQty < qty)) {
        txn.option_trans_code = "BTO";
        pos.longQty += qty;
      } else {
        txn.option_trans_code = "BTC";
        pos.shortQty = Math.max(0, pos.shortQty - qty);
      }
    }

    open.set(key, pos);
  }

  return adjusted;
}

export function buildTickerLotContext(
  ticker: string,
  txns: WheelTransaction[],
  options: BuildTickerLotOptions = {},
): TickerLotContext {
  const preserveShareHistory = options.preserveShareHistory === true;
  const key = normalizeTicker(ticker);
  const positions = new Map<string, AvgPosition>();
  const lotLedger: LotLedgerEvent[] = [];
  const handledShareEvents = new Set<string>();
  let lotStartDate = "";
  let currentLotStartDate = "";

  const classified = classifyOptionTxns(txns);
  const byId = new Map(
    classified
      .filter((txn): txn is WheelTransaction & { id: number } => txn.id != null)
      .map((txn) => [txn.id, txn]),
  );

  const snapshot = (
    txn: Pick<WheelTransaction, "account_id" | "ticker">,
  ): { netShares: number; avgPrice: number } => {
    const pos = positions.get(shareAvgPositionKey(txn, key)) ?? { qty: 0, avg: 0 };
    return { netShares: pos.qty, avgPrice: pos.avg };
  };

  for (const txn of classified) {
    if (normalizeTicker(txn.ticker ?? "") !== key) continue;

    const prevQty = positions.get(shareAvgPositionKey(txn, key))?.qty ?? 0;
    let ledgerEvent: LotLedgerEvent | null = null;

    if (txn.instrument_type === "stock") {
      const qty = Number(txn.quantity) || 0;
      const price = Number(txn.avg_price) || 0;

      if (txn.assignment_companion) {
        const linked = findLinkedAssignmentOption(txn, classified, byId);
        if (txn.action === "sell" && qty > 0 && txn.trade_date && lotStartDate) {
          const eventKind = linked?.side === "call" ? "call_assignment" : "stock_sell";
          const eventKey = shareEventKey(txn.trade_date, qty, eventKind);
          if (!handledShareEvents.has(eventKey)) {
            handledShareEvents.add(eventKey);
            applyAvgCostEvent({ ...txn, assignment_companion: false }, positions);
            const after = snapshot(txn);
            const strike = Number(linked?.strike) || 0;
            ledgerEvent = {
              date: txn.trade_date,
              kind: linked?.side === "call" ? "call_assignment" : "stock_sell",
              label: linked?.side === "call" ? "Call assignment" : "Stock sell",
              shares: -qty,
              price: strike > 0 ? strike : price,
              netShares: after.netShares,
              avgPrice: after.avgPrice,
            };
          }
        } else if (txn.action === "buy" && qty > 0 && txn.trade_date) {
          const eventKind = linked?.side === "put" ? "put_assignment" : "buy";
          const eventKey = shareEventKey(txn.trade_date, qty, eventKind);
          if (!handledShareEvents.has(eventKey)) {
            handledShareEvents.add(eventKey);
            if (prevQty === 0) {
              lotStartDate = txn.trade_date;
              currentLotStartDate = txn.trade_date;
            }
            applyAvgCostEvent({ ...txn, assignment_companion: false }, positions);
            const after = snapshot(txn);
            const strike = Number(linked?.strike) || 0;
            ledgerEvent = {
              date: txn.trade_date,
              kind: linked?.side === "put" ? "put_assignment" : "buy",
              label: linked?.side === "put" ? "Put assignment" : "Stock buy",
              shares: qty,
              price: strike > 0 ? strike : price,
              netShares: after.netShares,
              avgPrice: after.avgPrice,
            };
          }
        }
      } else if (txn.action === "buy" && qty > 0 && txn.trade_date) {
        if (prevQty === 0) {
          lotStartDate = txn.trade_date;
          currentLotStartDate = txn.trade_date;
        }
        applyAvgCostEvent(txn, positions);
        const after = snapshot(txn);
        ledgerEvent = {
          date: txn.trade_date,
          kind: "buy",
          label: "Stock buy",
          shares: qty,
          price,
          netShares: after.netShares,
          avgPrice: after.avgPrice,
        };
      } else if (txn.action === "sell" && qty > 0 && txn.trade_date && lotStartDate) {
        const linked = findLinkedAssignmentOption(txn, classified, byId);
        const isImpliedLeg =
          txn.implied_stock_leg === true || (Number(txn.id) < 0 && txn.instrument_type === "stock");
        const isCallAssignmentSell = linked?.side === "call" || (isImpliedLeg && linked?.side !== "put");
        if (isCallAssignmentSell) {
          const eventKey = shareEventKey(txn.trade_date, qty, "call_assignment");
          if (!handledShareEvents.has(eventKey)) {
            handledShareEvents.add(eventKey);
            applyAvgCostEvent(txn, positions);
            const after = snapshot(txn);
            const strike = Number(linked?.strike) || 0;
            ledgerEvent = {
              date: txn.trade_date,
              kind: "call_assignment",
              label: "Call assignment",
              shares: -qty,
              price: strike > 0 ? strike : price,
              netShares: after.netShares,
              avgPrice: after.avgPrice,
            };
          }
        } else {
          applyAvgCostEvent(txn, positions);
          const after = snapshot(txn);
          ledgerEvent = {
            date: txn.trade_date,
            kind: "stock_sell",
            label: "Stock sell",
            shares: -qty,
            price,
            netShares: after.netShares,
            avgPrice: after.avgPrice,
          };
        }
      }
    } else if (isPutAssignmentTxn(txn) && txn.trade_date) {
      const contracts = Number(txn.quantity) || 0;
      const strike = Number(txn.strike) || 0;
      const shares = Math.round(contracts * 100);
      if (shares > 0 && strike > 0) {
        const eventKey = shareEventKey(txn.trade_date, shares, "put_assignment");
        if (!handledShareEvents.has(eventKey)) {
          handledShareEvents.add(eventKey);
          if (prevQty === 0) {
            lotStartDate = txn.trade_date;
            currentLotStartDate = txn.trade_date;
          }
          applyAvgCostEvent(txn, positions);
          const after = snapshot(txn);
          ledgerEvent = {
            date: txn.trade_date,
            kind: "put_assignment",
            label: "Put assignment",
            shares,
            price: strike,
            netShares: after.netShares,
            avgPrice: after.avgPrice,
          };
        }
      }
    } else if (isCallAssignmentTxn(txn) && txn.trade_date) {
      const contracts = Number(txn.quantity) || 0;
      const strike = Number(txn.strike) || 0;
      const shares = Math.round(contracts * 100);
      if (shares > 0 && strike > 0 && (lotStartDate || prevQty > 0)) {
        const eventKey = shareEventKey(txn.trade_date, shares, "call_assignment");
        if (!handledShareEvents.has(eventKey)) {
          handledShareEvents.add(eventKey);
          applyAvgCostEvent(txn, positions);
          const after = snapshot(txn);
          ledgerEvent = {
            date: txn.trade_date,
            kind: "call_assignment",
            label: "Call assignment",
            shares: -shares,
            price: strike,
            netShares: after.netShares,
            avgPrice: after.avgPrice,
          };
        }
      }
    }

    if (ledgerEvent) lotLedger.push(ledgerEvent);

    if ((positions.get(key)?.qty ?? 0) === 0) {
      lotStartDate = "";
      handledShareEvents.clear();
      if (!preserveShareHistory) {
        lotLedger.length = 0;
      }
    }
  }

  const { records: unassignedCalls, totalPremium: callPremiumCollected } =
    buildUnassignedCallHistory(key, currentLotStartDate || lotStartDate, txns);

  return {
    lotStartDate: currentLotStartDate || lotStartDate,
    lotLedger,
    callPremiumCollected,
    unassignedCalls,
  };
}

function buildUnassignedCallHistory(
  ticker: string,
  lotStartDate: string,
  txns: WheelTransaction[],
): { records: UnassignedCallRecord[]; totalPremium: number } {
  if (!lotStartDate) return { records: [], totalPremium: 0 };

  interface ContractLedger {
    strike: number;
    expiration: string;
    contracts: number;
    openDate: string;
    closeDate: string;
    closeLabel: string;
    stoCredits: number;
    btcDebits: number;
    assigned: boolean;
    shortQty: number;
  }

  const contracts = new Map<string, ContractLedger>();
  const classified = classifyOptionTxns(txns);

  for (const txn of classified) {
    if (normalizeTicker(txn.ticker ?? "") !== ticker) continue;
    if (txn.side !== "call") continue;
    if ((txn.trade_date ?? "") < lotStartDate) continue;

    const key = contractKey(txn);
    if (!key) continue;

    const qty = txnContracts(txn);
    if (qty <= 0) continue;

    const ledger = contracts.get(key) ?? {
      strike: Number(txn.strike) || 0,
      expiration: txn.exp_date ?? "",
      contracts: 0,
      openDate: "",
      closeDate: "",
      closeLabel: "",
      stoCredits: 0,
      btcDebits: 0,
      assigned: false,
      shortQty: 0,
    };

    if (isAssignedOptionTxn(txn) || txn.close_reason === "assigned") {
      ledger.assigned = true;
      ledger.shortQty = Math.max(0, ledger.shortQty - qty);
      contracts.set(key, ledger);
      continue;
    }

    const code = txnCode(txn);
    const tradeDate = txn.trade_date ?? "";

    if (code === "STO") {
      if (!ledger.openDate) ledger.openDate = tradeDate;
      ledger.stoCredits += txnNotional(txn);
      ledger.shortQty += qty;
      ledger.contracts = Math.max(ledger.contracts, ledger.shortQty);
    } else if (code === "BTC") {
      ledger.btcDebits += txnNotional(txn);
      ledger.shortQty = Math.max(0, ledger.shortQty - qty);
      if (ledger.shortQty === 0) {
        ledger.closeDate = tradeDate;
        ledger.closeLabel = "Buy to close";
      }
    } else if (code === "OEXP" || txn.close_reason === "expired") {
      ledger.shortQty = Math.max(0, ledger.shortQty - qty);
      if (ledger.shortQty === 0) {
        ledger.closeDate = tradeDate || ledger.expiration;
        ledger.closeLabel = "Expired";
      }
    }

    contracts.set(key, ledger);
  }

  const records: UnassignedCallRecord[] = [];
  let totalPremium = 0;

  for (const ledger of contracts.values()) {
    if (ledger.assigned || ledger.stoCredits <= 0) continue;

    const netPremium = ledger.stoCredits - ledger.btcDebits;
    totalPremium += netPremium;

    records.push({
      openDate: ledger.openDate,
      closeDate: ledger.shortQty > 0 ? "" : ledger.closeDate || ledger.expiration,
      closeLabel: ledger.shortQty > 0 ? "Open" : ledger.closeLabel || "Closed",
      strike: ledger.strike,
      expiration: ledger.expiration,
      contracts: ledger.contracts,
      openCredit: ledger.stoCredits,
      closeCost: ledger.btcDebits,
      netPremium,
    });
  }

  records.sort((a, b) => {
    const closeCmp = String(b.closeDate || b.openDate).localeCompare(String(a.closeDate || a.openDate));
    if (closeCmp !== 0) return closeCmp;
    return String(b.openDate).localeCompare(String(a.openDate));
  });

  return { records, totalPremium };
}

function getCallsPlusTickerContext(
  stock: StockPosition,
  options: OptionPosition[],
  tickerCalls: WheelRecommendation[],
): { avgCost: number; currentPrice: number; shares: number } | null {
  return evaluateCallsPlusTicker(stock, options, tickerCalls);
}

function popSortValue(rec: WheelRecommendation): number {
  const pop = Number(rec.pop);
  return Number.isFinite(pop) ? pop : -1;
}

function popAsPercent(pop: number | null | undefined): number | null {
  const value = Number(pop);
  if (!Number.isFinite(value)) return null;
  return Math.abs(value) <= 1 ? value * 100 : value;
}

function meetsMinPop(rec: WheelRecommendation, minPopPct: number): boolean {
  if (minPopPct <= 0) return true;
  const pop = popAsPercent(rec.pop);
  return pop != null && pop >= minPopPct;
}

export { meetsMinPop, popAsPercent };

export const CALLS_MAX_DTE_KEY = "sfe-calls-max-dte";
export const CALLS_SHOW_RISKY_KEY = "sfe-calls-show-risky";
const CALLS_MAX_DTE_DEFAULT = 0;

export function meetsMaxDte(rec: WheelRecommendation, maxDte: number): boolean {
  if (maxDte <= 0) return true;
  const dte = rec.dte;
  return dte != null && Number.isFinite(dte) && dte >= 0 && dte <= maxDte;
}

export function loadCallsMaxDte(): number {
  try {
    const raw = localStorage.getItem(CALLS_MAX_DTE_KEY);
    if (raw == null || raw.trim() === "") return CALLS_MAX_DTE_DEFAULT;
    const value = Number(raw);
    return Number.isFinite(value) && value >= 0 ? value : CALLS_MAX_DTE_DEFAULT;
  } catch {
    return CALLS_MAX_DTE_DEFAULT;
  }
}

export function saveCallsMaxDte(value: number): void {
  localStorage.setItem(CALLS_MAX_DTE_KEY, String(value));
}

export function loadCallsShowRisky(): boolean {
  try {
    return localStorage.getItem(CALLS_SHOW_RISKY_KEY) === "1";
  } catch {
    return false;
  }
}

export function saveCallsShowRisky(value: boolean): void {
  localStorage.setItem(CALLS_SHOW_RISKY_KEY, value ? "1" : "0");
}

export function isRiskyCallRecommendation(rec: WheelRecommendation): boolean {
  const avgCost = rec.holding_avg_cost;
  const strike = rec.strike;
  if (avgCost == null || strike == null || !Number.isFinite(avgCost) || !Number.isFinite(strike)) {
    return false;
  }
  return callStrikeVsAvgCost(strike, avgCost) === "below";
}

export function filterRecommendationsByPopAndDte(
  recs: WheelRecommendation[],
  minPopPct: number,
  maxDte: number,
): WheelRecommendation[] {
  return recs.filter((rec) => meetsMinPop(rec, minPopPct) && meetsMaxDte(rec, maxDte));
}

export async function fetchRiskyCallRecommendations(force = false): Promise<WheelRecommendation[]> {
  const [positions, recommendations, txns] = await Promise.all([
    loadPositionsSnapshot(force),
    loadRecommendationsSnapshot(force),
    fetchCallsPlusTransactions((path) => wheelFetch(path), force),
  ]);

  let calls = mergeCallRecommendations(recommendations.calls, parseCallsTableDom());
  const marketdataPositions = blockedPositionsNeedingMarketdata(
    positions.stock,
    positions.options,
    calls,
  );

  if (marketdataPositions.length) {
    const batch = await fetchMarketdataCallsBatch(marketdataPositions, force);
    if (batch.recommendations.length) {
      calls = mergeCallRecommendations(calls, batch.recommendations);
    }
  }

  calls = applyTickerEarnings(calls, [...recommendations.calls, ...recommendations.puts]);

  const snapshot = buildCallsPlusSnapshot(
    positions.stock,
    positions.options,
    calls,
    txns,
  );
  return snapshot.rows.map((row) => ({
    ...row.rec,
    holding_avg_cost: row.rec.holding_avg_cost ?? row.avgCost,
    sfe_shares_held: row.shares,
  }));
}

export function loadCallsPlusMinPop(): number {
  try {
    const raw = localStorage.getItem(CALLS_PLUS_MIN_POP_KEY);
    if (raw == null || raw.trim() === "") return CALLS_PLUS_MIN_POP_DEFAULT;
    const value = Number(raw);
    return Number.isFinite(value) && value >= 0 ? value : CALLS_PLUS_MIN_POP_DEFAULT;
  } catch {
    return CALLS_PLUS_MIN_POP_DEFAULT;
  }
}

export function saveCallsPlusMinPop(value: number): void {
  localStorage.setItem(CALLS_PLUS_MIN_POP_KEY, String(value));
}

export function loadCallsPlusMaxDte(): number {
  try {
    const raw = localStorage.getItem(CALLS_PLUS_MAX_DTE_KEY);
    if (raw == null || raw.trim() === "") return CALLS_PLUS_MAX_DTE_DEFAULT;
    const value = Number(raw);
    return Number.isFinite(value) && value >= 0 ? value : CALLS_PLUS_MAX_DTE_DEFAULT;
  } catch {
    return CALLS_PLUS_MAX_DTE_DEFAULT;
  }
}

export function saveCallsPlusMaxDte(value: number): void {
  localStorage.setItem(CALLS_PLUS_MAX_DTE_KEY, String(value));
}

function loadCollapsedTickers(): Set<string> {
  try {
    const raw = localStorage.getItem(CALLS_PLUS_COLLAPSED_KEY);
    if (!raw) return new Set();
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return new Set();
    return new Set(parsed.map((t) => String(t || "").trim().toUpperCase()).filter(Boolean));
  } catch {
    return new Set();
  }
}

function saveCollapsedTickers(tickers: Set<string>): void {
  localStorage.setItem(CALLS_PLUS_COLLAPSED_KEY, JSON.stringify([...tickers]));
}

export function filterRowsByMinPop(rows: CallsPlusRow[], minPopPct: number): CallsPlusRow[] {
  if (minPopPct <= 0) return rows;
  return rows.filter((row) => meetsMinPop(row.rec, minPopPct));
}

export function filterRowsByMaxDte(rows: CallsPlusRow[], maxDte: number): CallsPlusRow[] {
  if (maxDte <= 0) return rows;
  return rows.filter((row) => meetsMaxDte(row.rec, maxDte));
}

function premiumSortValue(row: CallsPlusRow): number {
  const premium = recommendedCallPremium(row.rec, row.shares);
  return premium != null && Number.isFinite(premium) ? premium : -1;
}

/** 0 = ≤7 DTE (top focus), 1 = ≤15, 2 = longer, 3 = unknown. */
export function callsPlusDteTier(dte: number | null | undefined): number {
  if (dte == null || !Number.isFinite(dte) || dte < 0) return 3;
  if (dte <= CALLS_PLUS_DTE_PRIMARY) return 0;
  if (dte <= CALLS_PLUS_DTE_SECONDARY) return 1;
  return 2;
}

function rowDteTier(row: CallsPlusRow): number {
  return callsPlusDteTier(row.rec.dte);
}

function compareCallsPlusRows(a: CallsPlusRow, b: CallsPlusRow): number {
  return (
    rowDteTier(a) - rowDteTier(b) ||
    premiumSortValue(b) - premiumSortValue(a) ||
    popSortValue(b.rec) - popSortValue(a.rec) ||
    a.ticker.localeCompare(b.ticker) ||
    Number(a.rec.strike) - Number(b.rec.strike)
  );
}

/** Group by ticker; options by short DTE then premium; tickers by best short-dated premium. */
function groupRowsByMaxPremium(rows: CallsPlusRow[]): CallsPlusRow[][] {
  const order: string[] = [];
  const byTicker = new Map<string, CallsPlusRow[]>();
  for (const row of rows) {
    if (!byTicker.has(row.ticker)) order.push(row.ticker);
    const list = byTicker.get(row.ticker) ?? [];
    list.push(row);
    byTicker.set(row.ticker, list);
  }
  const groups = order.map((ticker) => {
    const list = byTicker.get(ticker) ?? [];
    list.sort(compareCallsPlusRows);
    return list;
  });
  groups.sort((a, b) => compareCallsPlusRows(a[0], b[0]));
  return groups;
}

function groupRowsByTicker(rows: CallsPlusRow[]): CallsPlusRow[][] {
  return groupRowsByMaxPremium(rows);
}

function atRiskOnAssignment(avgCost: number, strike: number, shares: number): number {
  if (!Number.isFinite(avgCost) || !Number.isFinite(strike) || shares <= 0) return 0;
  return Math.max(0, avgCost - strike) * shares;
}

function evenPriceAfterPremiums(
  avgCost: number,
  shares: number,
  cumulativeProfit: number,
): number {
  if (!Number.isFinite(avgCost) || shares <= 0) return avgCost;
  return avgCost - cumulativeProfit / shares;
}

export function buildCallsPlusSnapshot(
  stock: StockPosition[],
  options: OptionPosition[],
  calls: WheelRecommendation[],
  txns: WheelTransaction[],
): CallsPlusSnapshot {
  const lotByTicker = new Map<string, TickerLotContext>();
  const blockedByTicker = new Map<
    string,
    { avgCost: number; currentPrice: number; shares: number }
  >();

  let underwaterTickers = 0;
  for (const position of stock) {
    const ticker = normalizeTicker(position.ticker);
    if (!ticker) continue;

    const tickerCalls = callsForTicker(calls, ticker);
    const avgCost = resolveAvgCost(position, tickerCalls);
    const currentPrice = resolveCurrentPrice(ticker, position, options, tickerCalls);
    if (avgCost != null && currentPrice != null && currentPrice < avgCost) {
      underwaterTickers += 1;
    }

    const blocked = getCallsPlusTickerContext(position, options, tickerCalls);
    if (!blocked) continue;
    blockedByTicker.set(ticker, blocked);
    lotByTicker.set(ticker, buildTickerLotContext(ticker, txns));
  }

  const rows: CallsPlusRow[] = [];
  let riskyRecommendations = 0;

  for (const rec of calls) {
    const ticker = normalizeTicker(rec.underlying_ticker);
    const blocked = blockedByTicker.get(ticker);
    if (!blocked) continue;

    const avgCost = rec.holding_avg_cost ?? blocked.avgCost;
    const strike = Number(rec.strike);
    if (!Number.isFinite(strike) || callStrikeVsAvgCost(strike, avgCost) !== "below") continue;
    riskyRecommendations += 1;

    const lot = lotByTicker.get(ticker) ?? {
      lotStartDate: "",
      lotLedger: [],
      callPremiumCollected: 0,
      unassignedCalls: [],
    };

    rows.push({
      rec,
      ticker,
      shares: blocked.shares,
      avgCost,
      currentPrice: blocked.currentPrice,
      atRiskAmt: atRiskOnAssignment(avgCost, strike, blocked.shares),
      evenPrice: evenPriceAfterPremiums(avgCost, blocked.shares, lot.callPremiumCollected),
      cumulativeProfit: lot.callPremiumCollected,
      lot,
    });
  }

  rows.sort(compareCallsPlusRows);

  const blockedTickers = blockedByTicker.size;
  const totalUnderwater = [...blockedByTicker.values()].reduce(
    (sum, item) => sum + (item.avgCost - item.currentPrice) * item.shares,
    0,
  );
  const totalPremium = [...lotByTicker.values()].reduce(
    (sum, lot) => sum + lot.callPremiumCollected,
    0,
  );

  return {
    rows,
    blockedTickers,
    totalUnderwater,
    totalPremium,
    diagnostics: {
      stockPositions: stock.filter((item) => (Number(item.quantity) || 0) > 0).length,
      underwaterTickers,
      blockedTickers,
      callRecommendations: calls.length,
      riskyRecommendations,
    },
  };
}

function renderUnassignedCallHistory(
  calls: UnassignedCallRecord[],
  avgCost: number,
  ticker: string,
): string {
  if (!calls.length) {
    return `<p class="sfe-calls-plus-empty-history">No unassigned call premium since the current lot started.</p>`;
  }

  const columns = getCallsPlusHistoryColumns();
  const visible = visibleTableColumns(columns);

  const rows = calls
    .map((call) => {
      const vs = callStrikeVsAvgCost(call.strike, avgCost);
      const strikeClass = callStrikeVsAvgValueClass(vs);
      const strikeTip = callStrikeVsAvgTip(call.strike, avgCost, call.contracts * 100);
      const strikeTipPopover = renderCallStrikeTipPopover(
        call.strike,
        avgCost,
        call.contracts * 100,
      );
      const statusClass =
        call.closeLabel === "Open"
          ? "sfe-cp-call-open"
          : call.closeLabel === "Expired"
            ? "sfe-cp-call-expired"
            : "sfe-cp-call-btc";

      const cells: Record<string, string> = {
        open: `<td>${esc(call.openDate || "—")}</td>`,
        broker_mark: brokerMarkCellHtml(recommendationLedgerMark(ticker)),
        account_short: accountShortCellHtml(recommendationLedgerMark(ticker)),
        close: `<td>${esc(call.closeDate || "—")}</td>`,
        status: `<td class="${statusClass}">${esc(call.closeLabel)}</td>`,
        strike: `<td class="num ${strikeClass} sfe-data-tip-host"${strikeTip ? ` aria-label="${esc(strikeTip)}"` : ""}>${fmtUsd(call.strike)}${strikeTipPopover}</td>`,
        expiry: `<td>${esc(call.expiration || "—")}</td>`,
        qty: `<td class="num">${fmtNum(call.contracts)}</td>`,
        premium: `<td class="num roi-strong">${fmtUsd(call.openCredit)}</td>`,
        btc: `<td class="num roi-loss">${call.closeCost > 0 ? fmtUsd(call.closeCost) : "—"}</td>`,
        net: `<td class="num roi-strong">${fmtUsd(call.netPremium)}</td>`,
      };

      return `<tr>${visible.map((column) => cells[column.key] ?? "<td>—</td>").join("")}</tr>`;
    })
    .join("");

  return `
    <div class="sfe-calls-plus-history-scroll">
      <table class="sfe-calls-plus-history-table sfe-calls-plus-calls-history-table">
        ${renderTableColgroup(columns)}
        <thead>
          <tr>${renderTableHeadRow(columns)}</tr>
        </thead>
        <tbody>${rows}</tbody>
      </table>
    </div>`;
}

function fmtShareDelta(shares: number): string {
  if (shares > 0) return `+${fmtNum(shares)}`;
  return fmtNum(shares);
}

function shareDeltaClass(shares: number): string {
  if (shares > 0) return "roi-strong";
  if (shares < 0) return "roi-loss";
  return "";
}

function renderLotLedger(events: LotLedgerEvent[], ticker: string): string {
  if (!events.length) {
    return `<p class="sfe-calls-plus-empty-history">No lot transactions found for the current holding.</p>`;
  }

  const columns = getCallsPlusLedgerColumns();
  const visible = visibleTableColumns(columns);

  const rows = events
    .map((event) => {
      const cells: Record<string, string> = {
        date: `<td>${esc(event.date)}</td>`,
        broker_mark: brokerMarkCellHtml(recommendationLedgerMark(ticker)),
        account_short: accountShortCellHtml(recommendationLedgerMark(ticker)),
        event: `<td>${esc(event.label)}</td>`,
        shares: `<td class="num ${shareDeltaClass(event.shares)}">${fmtShareDelta(event.shares)}</td>`,
        price: `<td class="num">${fmtUsd(event.price)}</td>`,
        notional: `<td class="num">${fmtUsd(Math.abs(event.shares) * event.price)}</td>`,
        netShares: `<td class="num">${fmtNum(event.netShares)}</td>`,
        avgPrice: `<td class="num">${event.netShares > 0 ? fmtUsd(event.avgPrice) : "—"}</td>`,
      };
      return `<tr>${visible.map((column) => cells[column.key] ?? "<td>—</td>").join("")}</tr>`;
    })
    .join("");

  return `
    <div class="sfe-calls-plus-history-scroll">
      <table class="sfe-calls-plus-history-table sfe-calls-plus-ledger-table">
      ${renderTableColgroup(columns)}
      <thead>
        <tr>${renderTableHeadRow(columns)}</tr>
      </thead>
      <tbody>${rows}</tbody>
    </table>
    </div>`;
}

function renderMark(rec: WheelRecommendation): string {
  const mark = recMark(rec);
  return mark == null ? "—" : fmtUsd(mark);
}

function recMark(rec: WheelRecommendation): number | null {
  const mark = rec.last_price ?? rec.mid;
  return Number.isFinite(mark) && mark > 0 ? mark : null;
}

function recommendedCallPremiumPerContract(rec: WheelRecommendation): number | null {
  const price = rec.mid ?? rec.last_price;
  return Number.isFinite(price) && price > 0 ? price : null;
}

function recommendedCallContracts(rec: WheelRecommendation, shares: number): number {
  if (shares <= 0) return 0;
  const fromShares = Math.floor(shares / 100);
  const fromRec = callContractsAvailable(rec);
  return fromRec > 0 ? Math.min(fromShares, fromRec) : fromShares;
}

export function recommendedCallPremium(rec: WheelRecommendation, shares: number): number | null {
  const perContract = recommendedCallPremiumPerContract(rec);
  if (perContract == null) return null;
  const contracts = recommendedCallContracts(rec, shares);
  if (contracts <= 0) return null;
  return perContract * contracts * 100;
}

function renderRecommendedPremium(rec: WheelRecommendation, shares: number): string {
  const premium = recommendedCallPremium(rec, shares);
  return premium == null ? "—" : fmtUsd(premium);
}

function recForLedgerMenu(row: CallsPlusRow): WheelRecommendation {
  return {
    ...row.rec,
    underlying_ticker: row.ticker,
    side: row.rec.side || "call",
    holding_avg_cost: row.rec.holding_avg_cost ?? row.avgCost,
    underlying_price: row.rec.underlying_price ?? row.currentPrice,
  };
}

function recommendedPremiumTip(rec: WheelRecommendation, shares: number): string {
  const perContract = recommendedCallPremiumPerContract(rec);
  const contracts = recommendedCallContracts(rec, shares);
  if (perContract == null || contracts <= 0) {
    return "Premium if sold as recommended at mid for all available shares (mid × contracts × 100).";
  }
  return `Premium if sold as recommended: mid ${fmtUsd(perContract)} × ${fmtNum(contracts)} contract(s) × 100 = ${fmtUsd(perContract * contracts * 100)} for ${fmtNum(shares)} available shares.`;
}

function getCallsPlusMainColumns(): ColumnState[] {
  return loadTableColumns("calls-plus-main", getTableColumnDefaults("calls-plus-main"));
}

function syncCallsPlusColumnPicker(panel: HTMLElement, onApply: () => void): void {
  const reset = panel.querySelector<HTMLElement>(".sfe-calls-plus-filters .sfe-reset-filters");
  if (!reset) return;
  ensureColumnPickerBesideReset(reset, {
    tableId: "calls-plus-main",
    title: tableColumnTitle("calls-plus-main"),
    defaults: getTableColumnDefaults("calls-plus-main"),
    onApply,
  });
  panel.querySelector(".sfe-calls-plus-header-actions .sfe-column-settings-btn")?.remove();
}

function getCallsPlusHistoryColumns(): ColumnState[] {
  return loadTableColumns("calls-plus-history", getTableColumnDefaults("calls-plus-history"));
}

function getCallsPlusLedgerColumns(): ColumnState[] {
  return loadTableColumns("calls-plus-ledger", getTableColumnDefaults("calls-plus-ledger"));
}

function renderCallsPlusColgroup(columns: ColumnState[] = getCallsPlusMainColumns()): string {
  return renderTableColgroup(columns);
}

function renderCallsPlusDetailPanel(row: CallsPlusRow): string {
  const detailId = `sfe-calls-plus-detail-${row.ticker}`;
  const lotDate = row.lot.lotStartDate || "—";

  return `
    <div id="${detailId}" class="sfe-calls-plus-detail-panel" hidden data-ticker="${esc(row.ticker)}">
      <div class="sfe-calls-plus-detail">
        <div class="sfe-calls-plus-detail-section">
          <div class="sfe-calls-plus-detail-head">
            <strong>${esc(row.ticker)} lot transactions</strong>
            <span>Lot since ${esc(lotDate)} · ${fmtNum(row.shares)} shares · avg ${fmtUsd(row.avgCost)}</span>
          </div>
          ${renderLotLedger(row.lot.lotLedger, row.ticker)}
        </div>
        <div class="sfe-calls-plus-detail-section">
          <div class="sfe-calls-plus-detail-head">
            <strong>Prior unassigned calls</strong>
            <span>Since lot start · net premium ${fmtUsd(row.cumulativeProfit)}</span>
          </div>
          ${renderUnassignedCallHistory(row.lot.unassignedCalls, row.avgCost, row.ticker)}
        </div>
      </div>
    </div>`;
}

function renderCallsPlusMainCell(
  key: string,
  row: CallsPlusRow,
  showTickerMeta: boolean,
  columns: ColumnState[],
  opts: { collapsed?: boolean; collapsible?: boolean } = {},
): string {
  const collapsed = opts.collapsed === true;
  const collapsible = opts.collapsible === true;
  const vs = callStrikeVsAvgCost(row.rec.strike, row.avgCost);
  const strikeClass = callStrikeVsAvgValueClass(vs);
  const strikeTip = callStrikeVsAvgTip(row.rec.strike, row.avgCost, row.shares);
  const strikeTipPopover = renderCallStrikeTipPopover(row.rec.strike, row.avgCost, row.shares);

  switch (key) {
    case "expand":
      return `<td class="sfe-calls-plus-expand-cell">
        ${
          showTickerMeta && collapsible
            ? `<button type="button" class="sfe-calls-plus-expand" aria-expanded="${collapsed ? "false" : "true"}" data-action="toggle-group" data-ticker="${esc(row.ticker)}" title="${collapsed ? "Expand option rows" : "Collapse option rows"}">${collapsed ? "▸" : "▾"}</button>`
            : ""
        }
      </td>`;
    case "ticker":
      return `<td class="sfe-calls-plus-ticker">${
        showTickerMeta
          ? `<button type="button" class="sfe-calls-plus-ticker-btn" aria-expanded="false" aria-controls="sfe-calls-plus-detail-${esc(row.ticker)}" data-action="toggle-lot" data-ticker="${esc(row.ticker)}" title="Lot history"><strong>${esc(row.ticker)}</strong></button>`
          : ""
      }</td>`;
    case "broker_mark":
      return showTickerMeta
        ? brokerMarkCellHtml(recommendationLedgerMark(row.ticker))
        : `<td class="sfe-mark-col" data-col="broker_mark"></td>`;
    case "account_short":
      return showTickerMeta
        ? accountShortCellHtml(recommendationLedgerMark(row.ticker))
        : `<td class="sfe-mark-col sfe-acct-col" data-col="account_short"></td>`;
    case "shares":
      return `<td class="num">${showTickerMeta ? fmtNum(row.shares) : ""}</td>`;
    case "avgCost":
      return `<td class="num">${showTickerMeta ? fmtUsd(row.avgCost) : ""}</td>`;
    case "price":
      return `<td class="num roi-loss">${showTickerMeta ? fmtUsd(row.currentPrice) : ""}</td>`;
    case "atRisk":
      return `<td class="num roi-loss sfe-data-tip-host" data-tip="Loss if assigned at this strike: (avg cost − strike) × shares.">${fmtUsd(row.atRiskAmt)}</td>`;
    case "even":
      return `<td class="num sfe-data-tip-host" data-tip="Breakeven share price after unassigned call premium since the current lot started.">${showTickerMeta ? fmtUsd(row.evenPrice) : ""}</td>`;
    case "cumProfit":
      return `<td class="num roi-strong sfe-data-tip-host" data-tip="Net premium from calls that expired or were bought back — not assigned — since the current lot started.">${showTickerMeta ? fmtUsd(row.cumulativeProfit) : ""}</td>`;
    case "strike":
      return `<td class="num ${strikeClass} sfe-data-tip-host"${strikeTip ? ` aria-label="${esc(strikeTip)}"` : ""}>${fmtUsd(row.rec.strike)}${strikeTipPopover}</td>`;
    case "pop":
      return `<td class="num">${fmtPct(row.rec.pop)}</td>`;
    case "premium":
      return `<td class="num roi-strong sfe-data-tip-host" data-tip="${esc(recommendedPremiumTip(row.rec, row.shares))}">${renderRecommendedPremium(row.rec, row.shares)}</td>`;
    case "expiry":
      return `<td>${esc(row.rec.expiration_date ?? "—")}</td>`;
    case "dte":
      return `<td class="num">${fmtNum(row.rec.dte)}</td>`;
    case "mark":
      return `<td class="num">${renderMark(row.rec)}</td>`;
    case "roi":
      return `<td class="num ${roiClass(row.rec.roi)}">${fmtWheelRatio(row.rec.roi)}</td>`;
    case "actions":
      return `<td class="sfe-calls-plus-actions-cell">${renderFocusRecRowMenu(recForLedgerMenu(row))}</td>`;
    default:
      return `<td>—</td>`;
  }
}

function renderCallsPlusDataRow(
  row: CallsPlusRow,
  showTickerMeta: boolean,
  columns: ColumnState[] = getCallsPlusMainColumns(),
  opts: { collapsed?: boolean; optionRow?: boolean; collapsible?: boolean } = {},
): string {
  const visible = visibleTableColumns(columns);
  const cells = visible
    .map((column) =>
      renderCallsPlusMainCell(column.key, row, showTickerMeta, columns, {
        collapsed: opts.collapsed === true,
        collapsible: opts.collapsible === true,
      }),
    )
    .join("");
  const classes = [
    "sfe-calls-plus-row",
    opts.optionRow ? "sfe-calls-plus-row-option" : "",
  ]
    .filter(Boolean)
    .join(" ");
  const hidden = opts.optionRow && opts.collapsed ? " hidden" : "";
  return `<tr class="${classes}" data-ticker="${esc(row.ticker)}"${hidden}>${cells}</tr>`;
}

function renderTable(
  snapshot: CallsPlusSnapshot,
  minPopPct: number,
  maxDte: number,
  collapsedTickers: Set<string>,
): string {
  const rows = filterRowsByMaxDte(filterRowsByMinPop(snapshot.rows, minPopPct), maxDte);

  if (!rows.length) {
    const d = snapshot.diagnostics;
    const filterHints: string[] = [];
    if (minPopPct > 0 && snapshot.rows.length) {
      filterHints.push(`Min PoP ${fmtNum(minPopPct, 0)}%`);
    }
    if (maxDte > 0 && snapshot.rows.length) {
      filterHints.push(`Max DTE ${fmtNum(maxDte, 0)}`);
    }
    const popHint = filterHints.length
      ? `<p class="sfe-calls-plus-empty-hint">No recommendations met ${filterHints.join(" · ")} (${fmtNum(snapshot.rows.length)} below-avg rec(s) hidden by filters).</p>`
      : "";
    return `
      <div class="sfe-calls-plus-empty">
        <p>No higher-risk call recommendations matched the Calls+ filters.</p>
        ${popHint}
        <p class="sfe-calls-plus-empty-hint">
          Loaded ${fmtNum(d.stockPositions)} stock position(s),
          ${fmtNum(d.underwaterTickers)} underwater,
          ${fmtNum(d.blockedTickers)} eligible (underwater, shares free),
          ${fmtNum(d.callRecommendations)} call recommendation(s),
          ${fmtNum(d.riskyRecommendations)} with strike below average cost.
        </p>
        <p class="sfe-calls-plus-empty-hint">
          Calls+ shows underwater holdings with shares not tied to open call contracts, call strikes below your average share cost, POP at or above Min PoP %, and DTE at or below Max DTE (default 15; ranked ≤7 then ≤15). 0 Max DTE = any.
        </p>
        ${
          d.marketdata
            ? `<p class="sfe-calls-plus-empty-hint">
                Market Data lookup: ${fmtNum(d.marketdata.requestedTickers)} eligible ticker(s),
                ${fmtNum(d.marketdata.addedRecs)} supplemental rec(s),
                ${fmtNum(d.marketdata.creditsUsed)} credit(s) used${
                  d.marketdata.dailyCreditsRemaining != null
                    ? ` (${fmtNum(d.marketdata.dailyCreditsRemaining)} remaining today)`
                    : ""
                }${d.marketdata.cachedTickers ? `, ${fmtNum(d.marketdata.cachedTickers)} from cache` : ""}.
              </p>`
            : ""
        }
      </div>`;
  }

  const columns = getCallsPlusMainColumns();
  const groups = groupRowsByTicker(rows)
    .map((tickerRows) => {
      const first = tickerRows[0];
      const collapsed = collapsedTickers.has(first.ticker);
      const collapsible = tickerRows.length > 1;
      const body = tickerRows
        .map((row, index) =>
          renderCallsPlusDataRow(row, index === 0, columns, {
            collapsed,
            optionRow: index > 0,
            collapsible,
          }),
        )
        .join("");

      return `
        <div class="sfe-calls-plus-row-group${collapsed ? " is-collapsed" : ""}" data-ticker="${esc(first.ticker)}">
          <table class="sfe-calls-plus-table">
            ${renderCallsPlusColgroup(columns)}
            <tbody>${body}</tbody>
          </table>
          ${renderCallsPlusDetailPanel(first)}
        </div>`;
    })
    .join("");

  return `
    <div class="sfe-calls-plus-table-wrap">
      <table class="sfe-calls-plus-table sfe-calls-plus-table-head">
        ${renderCallsPlusColgroup(columns)}
        <thead>
          <tr>${renderTableHeadRow(columns)}</tr>
        </thead>
      </table>
      <div class="sfe-calls-plus-row-list">${groups}</div>
    </div>`;
}

function filtersHtml(minPopPct: number, maxDte: number): string {
  return `
    <label class="sfe-calls-plus-min-pop-filter">
      <span class="sfe-calls-plus-min-pop-label">
        Min PoP %
        <span class="hint" tabindex="0" role="img" data-tip="Show only recommendations with probability of profit at or above this threshold.">i</span>
      </span>
      <div class="sfe-calls-plus-min-pop-control">
        <input
          type="number"
          min="0"
          max="100"
          step="1"
          inputmode="numeric"
          data-calls-plus-min-pop
          value="${fmtNum(minPopPct, 0)}"
        />
        <span class="sfe-calls-plus-min-pop-suffix">%</span>
      </div>
    </label>
    <label class="sfe-calls-plus-min-pop-filter">
      <span class="sfe-calls-plus-min-pop-label">
        Max DTE
        <span class="hint" tabindex="0" role="img" data-tip="Hide recommendations with more days to expiry than this. Default 15 keeps the biweekly window; set 7 for weeklies only. 0 = any DTE.">i</span>
      </span>
      <div class="sfe-calls-plus-min-pop-control">
        <input
          type="number"
          min="0"
          max="365"
          step="1"
          inputmode="numeric"
          data-calls-plus-max-dte
          value="${fmtNum(maxDte, 0)}"
        />
        <span class="sfe-calls-plus-min-pop-suffix">d</span>
      </div>
    </label>`;
}

function bindMinPopHandler(panel: HTMLElement, onChange: () => void): void {
  const input = panel.querySelector<HTMLInputElement>("[data-calls-plus-min-pop]");
  if (!input || input.dataset.sfeBound) return;
  input.dataset.sfeBound = "true";

  const apply = (): void => {
    const parsed = Number(input.value);
    const next = Number.isFinite(parsed) && parsed >= 0 ? parsed : CALLS_PLUS_MIN_POP_DEFAULT;
    if (input.value !== String(next)) input.value = String(next);
    saveCallsPlusMinPop(next);
    onChange();
  };

  input.addEventListener("input", apply);
  input.addEventListener("change", apply);
}

function bindMaxDteHandler(panel: HTMLElement, onChange: () => void): void {
  const input = panel.querySelector<HTMLInputElement>("[data-calls-plus-max-dte]");
  if (!input || input.dataset.sfeBound) return;
  input.dataset.sfeBound = "true";

  const apply = (): void => {
    const parsed = Number(input.value);
    const next = Number.isFinite(parsed) && parsed >= 0 ? parsed : CALLS_PLUS_MAX_DTE_DEFAULT;
    if (input.value !== String(next)) input.value = String(next);
    saveCallsPlusMaxDte(next);
    onChange();
  };

  input.addEventListener("input", apply);
  input.addEventListener("change", apply);
}

function bindExpandHandlers(panel: HTMLElement, onToggleGroup: (ticker: string) => void): void {
  panel.querySelectorAll<HTMLButtonElement>("[data-action='toggle-group']").forEach((button) => {
    if (button.dataset.sfeBound) return;
    button.dataset.sfeBound = "true";
    button.addEventListener("click", (event) => {
      event.preventDefault();
      event.stopPropagation();
      const ticker = button.dataset.ticker ?? "";
      if (ticker) onToggleGroup(ticker);
    });
  });

  panel.querySelectorAll<HTMLButtonElement>("[data-action='toggle-lot']").forEach((button) => {
    if (button.dataset.sfeBound) return;
    button.dataset.sfeBound = "true";
    button.addEventListener("click", (event) => {
      event.preventDefault();
      event.stopPropagation();
      const ticker = button.dataset.ticker ?? "";
      const detail = panel.querySelector<HTMLElement>(
        `#sfe-calls-plus-detail-${CSS.escape(ticker)}`,
      );
      if (!detail) return;
      const open = detail.hidden;
      detail.hidden = !open;
      button.setAttribute("aria-expanded", open ? "true" : "false");
    });
  });
}

export class CallsPlusController {
  private panel: HTMLElement | null = null;
  private refreshing = false;
  private snapshotCache: CallsPlusSnapshot | null = null;
  private snapshotCacheAt = 0;
  private txnsCache: WheelTransaction[] = [];
  private txnsCacheAt = 0;
  private focusMenusBound = false;
  private minPopPct = loadCallsPlusMinPop();
  private maxDte = loadCallsPlusMaxDte();
  private collapsedTickers = loadCollapsedTickers();
  private marksCleanup: (() => void) | null = null;

  mount(panel: HTMLElement): void {
    this.panel = panel;
    this.marksCleanup?.();
    this.marksCleanup = subscribeLedgerMarks(() => {
      if (this.panel && this.snapshotCache) this.render(this.snapshotCache);
    });
    void ensureLedgerMarkIndex().catch(() => null);
    setFocusActionsRefresh(() => this.refresh(true));
    if (!this.focusMenusBound) {
      this.focusMenusBound = true;
      bindFocusRowMenuDocumentHandlers();
    }
    if (!panel.querySelector(".sfe-calls-plus-body")) {
      panel.innerHTML = `
        <div class="sfe-calls-plus-header">
          <div class="sfe-calls-plus-header-text">
            <h2 class="insight-h">Calls+ · Underwater covered-call risk</h2>
            <p>Higher-risk call recommendations (strike below average cost) for underwater shares not under open call contracts. Short-dated focus: weeklies (≤7 DTE) first, then ≤15 DTE. Filtered by Min PoP % and Max DTE.</p>
          </div>
          <div class="sfe-calls-plus-header-actions"></div>
        </div>
        <div class="sfe-calls-plus-filters">
          ${filtersHtml(this.minPopPct, this.maxDte)}
        </div>
        <div class="sfe-calls-plus-body">
          <div class="sfe-calls-plus-kpis"></div>
          <div class="sfe-calls-plus-content"></div>
        </div>`;
    } else {
      const filters = panel.querySelector(".sfe-calls-plus-filters");
      if (filters) {
        filters.innerHTML = filtersHtml(this.minPopPct, this.maxDte);
      } else {
        const header = panel.querySelector(".sfe-calls-plus-header");
        const body = panel.querySelector(".sfe-calls-plus-body");
        if (header && body) {
          const el = document.createElement("div");
          el.className = "sfe-calls-plus-filters";
          el.innerHTML = filtersHtml(this.minPopPct, this.maxDte);
          header.insertAdjacentElement("afterend", el);
        }
      }
      const subtitle = panel.querySelector(".sfe-calls-plus-header-text p");
      if (subtitle) {
        subtitle.textContent =
          "Higher-risk call recommendations (strike below average cost) for underwater shares not under open call contracts. Short-dated focus: weeklies (≤7 DTE) first, then ≤15 DTE. Filtered by Min PoP % and Max DTE.";
      }
    }

    const headerActions = panel.querySelector<HTMLElement>(".sfe-calls-plus-header-actions");
    headerActions?.querySelector(".sfe-column-settings-btn")?.remove();

    void import("./filterReset").then(({ syncResetButtons }) => syncResetButtons());
    syncCallsPlusColumnPicker(panel, () => {
      if (this.snapshotCache) this.render(this.snapshotCache);
    });

    bindMinPopHandler(this.panel, () => {
      const input = this.panel?.querySelector<HTMLInputElement>("[data-calls-plus-min-pop]");
      const parsed = Number(input?.value);
      this.minPopPct =
        Number.isFinite(parsed) && parsed >= 0 ? parsed : CALLS_PLUS_MIN_POP_DEFAULT;
      if (this.snapshotCache) this.render(this.snapshotCache);
    });
    bindMaxDteHandler(this.panel, () => {
      const input = this.panel?.querySelector<HTMLInputElement>("[data-calls-plus-max-dte]");
      const parsed = Number(input?.value);
      this.maxDte =
        Number.isFinite(parsed) && parsed >= 0 ? parsed : CALLS_PLUS_MAX_DTE_DEFAULT;
      if (this.snapshotCache) this.render(this.snapshotCache);
    });
    if (this.snapshotCache) this.render(this.snapshotCache);
  }

  async refresh(force = false): Promise<void> {
    if (!this.panel || this.refreshing) return;
    this.refreshing = true;

    const content = this.panel.querySelector(".sfe-calls-plus-content");
    if (content && !this.snapshotCache) {
      content.innerHTML = `<p class="sfe-calls-plus-loading">Loading Calls+…</p>`;
    }

    try {
      const now = Date.now();
      const [positions, recommendations, txns] = await Promise.all([
        loadPositionsSnapshot(force),
        loadRecommendationsSnapshot(force),
        this.loadTransactions(force, now),
      ]);

      let calls = mergeCallRecommendations(recommendations.calls, parseCallsTableDom());

      const marketdataPositions = blockedPositionsNeedingMarketdata(
        positions.stock,
        positions.options,
        calls,
      );
      let marketdataMeta: CallsPlusDiagnostics["marketdata"] | undefined;

      if (marketdataPositions.length) {
        const batch = await fetchMarketdataCallsBatch(marketdataPositions, force);
        if (batch.recommendations.length) {
          calls = mergeCallRecommendations(calls, batch.recommendations);
        }
        marketdataMeta = {
          requestedTickers: marketdataPositions.length,
          creditsUsed: batch.creditsUsed,
          dailyCreditsRemaining: batch.dailyCreditsRemaining,
          addedRecs: batch.recommendations.length,
          cachedTickers: batch.perTicker?.filter((row) => row.cached).length,
        };
      }

      const snapshot = buildCallsPlusSnapshot(
        positions.stock,
        positions.options,
        calls,
        txns,
      );
      if (marketdataMeta) {
        snapshot.diagnostics = { ...snapshot.diagnostics, marketdata: marketdataMeta };
      }
      this.snapshotCache = snapshot;
      this.snapshotCacheAt = now;
      this.render(this.snapshotCache);
    } catch {
      showToast("Could not load Calls+ data.", "error");
      if (this.snapshotCache) this.render(this.snapshotCache);
    } finally {
      this.refreshing = false;
    }
  }

  private async loadTransactions(force: boolean, now: number): Promise<WheelTransaction[]> {
    if (!force && now - this.txnsCacheAt < DATA_CACHE_MS && this.txnsCache.length) {
      return this.txnsCache;
    }
    const txns = await fetchTransactions();
    this.txnsCache = txns;
    this.txnsCacheAt = now;
    return txns;
  }

  private render(snapshot: CallsPlusSnapshot): void {
    if (!this.panel) return;

    const visibleRows = filterRowsByMaxDte(
      filterRowsByMinPop(snapshot.rows, this.minPopPct),
      this.maxDte,
    );
    const kpis = this.panel.querySelector(".sfe-calls-plus-kpis");
    const content = this.panel.querySelector(".sfe-calls-plus-content");
    const minPopInput = this.panel.querySelector<HTMLInputElement>("[data-calls-plus-min-pop]");
    const maxDteInput = this.panel.querySelector<HTMLInputElement>("[data-calls-plus-max-dte]");
    if (minPopInput && document.activeElement !== minPopInput) {
      minPopInput.value = String(this.minPopPct);
    }
    if (maxDteInput && document.activeElement !== maxDteInput) {
      maxDteInput.value = String(this.maxDte);
    }
    if (!kpis || !content) return;

    kpis.innerHTML = `
      <div class="sfe-custom-stats sfe-calls-plus-stats">
        <div class="sfe-custom-stat">
          <span>Eligible tickers</span>
          <strong>${fmtNum(snapshot.blockedTickers)}</strong>
        </div>
        <div class="sfe-custom-stat">
          <span>Underwater (shares)</span>
          <strong class="roi-loss">${fmtUsd(snapshot.totalUnderwater)}</strong>
        </div>
        <div class="sfe-custom-stat">
          <span>Cum. profit (unassigned)</span>
          <strong class="roi-strong">${fmtUsd(snapshot.totalPremium)}</strong>
        </div>
        <div class="sfe-custom-stat">
          <span>Matching recs</span>
          <strong>${fmtNum(visibleRows.length)}</strong>
        </div>
      </div>`;

    content.innerHTML = renderTable(
      snapshot,
      this.minPopPct,
      this.maxDte,
      this.collapsedTickers,
    );
    bindExpandHandlers(this.panel, (ticker) => this.toggleGroup(ticker));
    syncFocusRowMenuAfterDomUpdate();
  }

  private toggleGroup(ticker: string): void {
    const key = ticker.trim().toUpperCase();
    if (!key) return;
    if (this.collapsedTickers.has(key)) this.collapsedTickers.delete(key);
    else this.collapsedTickers.add(key);
    saveCollapsedTickers(this.collapsedTickers);
    if (this.snapshotCache) this.render(this.snapshotCache);
  }
}
